import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import * as IPurchases from '../../../../../interfaces/merchants/products/purchases';
import { UserMeReduxState } from '../../../../../interfaces/users';
import { inputPurchasingPayment } from '../../../../../redux/actions/merchants/products/purchases/inputs';
import PaymentCreditCard, {
  submittable as creditCardSubmittable,
} from './components/PaymentCreditCard';
import PaymentMethodsSelector from '../PaymentMethodsSelector';
import { getMe } from '../../../../../redux/selectors/users';
import { ReduxState } from '../../../../../redux/reducers';
import { getInputs } from '../../../../../redux/selectors/merchants/products/purchases/inputs';
import { CreditCard } from '../../../../../interfaces/creditCard';
import { useScrollToTop } from '../../../../../hooks/useScrollToTop';
import { useAppDispatch } from '../../../../../redux';
import { RSButton } from 'src/reservation-page/RSButton';

interface ElementProps {
  schemeType: IPurchases.SchemeType;
  settings: IPurchases.SettingPayment;
  postable: boolean;
  merchantPublicId: string;
  onSubmit: () => void;
}

const createSubmittable = (
  me: UserMeReduxState,
  creditCard: CreditCard | undefined,
  selection: IPurchases.PaymentSelection,
): boolean => {
  if (!me.creditCard) {
    return !!creditCard && creditCardSubmittable(creditCard);
  }
  if (!selection) {
    return false;
  }
  if (selection === IPurchases.PaymentSelection.UsedCreditCard) {
    return true;
  }
  return !!creditCard && creditCardSubmittable(creditCard);
};

const Payment: React.FC<ElementProps> = ({
  schemeType,
  settings,
  postable,
  onSubmit,
  merchantPublicId,
}) => {
  const intl = useIntl();
  useScrollToTop();
  const dispatch = useAppDispatch();
  const [submittable, setSubmittable] = useState<boolean>(false);
  const [selection, setSelection] = useState<IPurchases.PaymentSelection>(
    IPurchases.PaymentSelection.UsedCreditCard,
  );
  const [creditCard, setCreditCard] = useState<CreditCard | undefined>(
    undefined,
  );

  const me = useSelector(
    (state: ReduxState) => getMe(state.users),
    shallowEqual,
  );
  const inputs = useSelector(
    (state: ReduxState) => getInputs(state.merchantsProductsPurchasesInputs),
    shallowEqual,
  );

  useEffect(() => {
    const init = () => {
      setCreditCard(inputs.payment ? inputs.payment.creditCard : undefined);
      setSelection(
        me.creditCard
          ? IPurchases.PaymentSelection.UsedCreditCard
          : IPurchases.PaymentSelection.AnotherCreditCard,
      );
      setSubmittable(createSubmittable(me, creditCard, selection));
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = useCallback(
    (selection: IPurchases.PaymentSelection) => {
      setSelection(selection);
      setSubmittable(createSubmittable(me, creditCard, selection));
    },
    [me, creditCard],
  );

  const handleChange = useCallback(
    (creditCard: CreditCard) => {
      setCreditCard(creditCard);
      setSubmittable(createSubmittable(me, creditCard, selection));
    },
    [me, selection],
  );

  const handleSubmit = useCallback(async () => {
    try {
      await dispatch(
        inputPurchasingPayment(
          schemeType,
          inputs.productId!,
          {
            creditCard,
            selection,
          },
          merchantPublicId,
        ),
      );
      onSubmit();
    } catch (e) {
      // failed
    }
  }, [
    dispatch,
    schemeType,
    inputs.productId,
    onSubmit,
    creditCard,
    selection,
    merchantPublicId,
  ]);

  const showInputCreditCard = useMemo<boolean>((): boolean => {
    if (!me.creditCard) return true;
    if (!selection) return false;
    return selection === IPurchases.PaymentSelection.AnotherCreditCard;
  }, [me, selection]);

  return (
    <>
      <PaymentMethodsSelector
        me={me}
        selection={selection}
        onSelect={handleSelect}
      />
      {showInputCreditCard && (
        <PaymentCreditCard
          error={settings.errors ? settings.errors.creditCard : undefined}
          creditCard={creditCard || {}}
          onChange={handleChange}
        />
      )}
      <div className="px-[18px] py-5">
        <RSButton
          variant="basic"
          isBlock
          isLoading={!postable}
          disabled={!submittable || !postable}
          value={intl.formatMessage({
            id: 'purchases.payment.submitButton',
          })}
          onClick={() => handleSubmit()}
        >
          {intl.formatMessage({
            id: 'purchases.payment.submitButton',
          })}
        </RSButton>
      </div>
    </>
  );
};

export default Payment;
